import {
  BaseNote,
  getMidiNumberFromNoteTuple,
  noteFromOsmdPitch,
} from "Phaser/Util/OSMDHelper";
import { ClefType, Config, CursorData } from "./Types";
import { DefaultConfig, MinimumConfig } from ".";
import { getNoteLineNumber } from "./Clef";

export type ClefHeight = {
  /* For the treble clef: the 'inner height' is measured from the highest pitch line
  to the lowest pitch line including ledger lines.
  For the bass clef: the 'inner height' is measured from the lowest pitch line
  to the highest pitch line including ledger lines.*/
  innerHeight: number;
  /* The outer height is measured from the highest pitch line (including ledger lines)
   * to the lowest pitch line (including ledger lines) */
  outerHeight: number;
};

function calculateNumberOfLinesInClef(
  cursorData: CursorData["serializedIterator"], 
  clef: ClefType,
  //numExtraLinesTop: number,
  //numExtraLinesBottom: number,
): number {
  let minNote = [Infinity, ["C", Infinity]];
  let maxNote = [-Infinity, ["C", -Infinity]];
  cursorData.forEach(({ currentVoiceEntries }) =>
    currentVoiceEntries.forEach(({ Notes }) =>
      Notes.forEach(({ pitch, isRest }) => {
        if (isRest) return;
        // get midi number from pitch
        const note = noteFromOsmdPitch(pitch);
        const midiNumber = getMidiNumberFromNoteTuple(note);
        if (midiNumber < (minNote[0] as number)) {
          minNote = [midiNumber, note];
        }
        if (midiNumber > (maxNote[0] as number)) {
          maxNote = [midiNumber, note];
        }
      }),
    ),
  );
  const clefDefaultLowestLineNumber = 8;
  const clefDefaultHighestLineNumber = 0;
  const _minNoteLineNumber = getNoteLineNumber(
    0,
    clef,
    minNote[1] as [BaseNote, number],
  );
  const minNoteLineNumber = 14;
  const _maxNoteLineNumber = getNoteLineNumber(
    0,
    clef,
    maxNote[1] as [BaseNote, number],
  );
  const maxNoteLineNumber = -4;
  const outerHeightLineDiff =
    Math.max(minNoteLineNumber, clefDefaultLowestLineNumber) -
    Math.min(maxNoteLineNumber, clefDefaultHighestLineNumber);
  console.debug("[PhraseOptimizer::calculateNumberOfLinesInClef] outerHeightLineDiff", outerHeightLineDiff, clef);
  return outerHeightLineDiff;
}

export function computeClefHeightWithExtraLines(
  clef: ClefType, 
  config: Config, 
  numExtraLinesTop: number, 
  numExtraLinesBottom: number
): ClefHeight {
  const defaultNumberOfLines = 8;
  const numberOfLines = defaultNumberOfLines + numExtraLinesTop + numExtraLinesBottom;
  const outerHeightLineDiff = numberOfLines;
  const innerHeightLineDiff = clef === 'treble' ? numberOfLines - numExtraLinesTop : numberOfLines - numExtraLinesBottom;
  const outerHeight = (outerHeightLineDiff) / 2 * config.pitchLineGap;
  const innerHeight = (innerHeightLineDiff) / 2 * config.pitchLineGap;
  return {
    innerHeight,
    outerHeight,
  };
}

export function computeClefHeight(
  cursorData: CursorData["serializedIterator"],
  clef: ClefType,
  config: Config,
): ClefHeight {
  const { pitchLineGap } = config;
  let minNote: [number, [BaseNote, number]] | undefined;
  let maxNote: [number, [BaseNote, number]] | undefined;
  cursorData.forEach(({ currentVoiceEntries }) =>
    currentVoiceEntries.forEach(({ Notes }) =>
      Notes.forEach(({ pitch, isRest, staffPosition }) => {
        if (isRest || (clef === 'treble' ? staffPosition != 'top' : staffPosition != 'bottom')) return;
        // get midi number from pitch
        const note = noteFromOsmdPitch(pitch);
        const midiNumber = getMidiNumberFromNoteTuple(note);
        if (minNote === undefined || midiNumber < (minNote[0] as number)) {
          minNote = [midiNumber, note];
        }
        if (maxNote === undefined || midiNumber > (maxNote[0] as number)) {
          maxNote = [midiNumber, note];
        }
      }),
    ),
  );
  const clefDefaultLowestLineNumber = 8;
  const clefDefaultHighestLineNumber = 0;
  const _minNoteLineNumber = minNote != undefined ? getNoteLineNumber(
    0,
    clef,
    minNote[1] as [BaseNote, number],
  ) : clefDefaultLowestLineNumber;
  const _maxNoteLineNumber = maxNote != undefined ? getNoteLineNumber(
    0,
    clef,
    maxNote[1] as [BaseNote, number],
  ) : clefDefaultHighestLineNumber;
  const minNoteLineNumber = 14;
  const maxNoteLineNumber = -4;
  const outerHeightLineDiff =
    Math.max(minNoteLineNumber, clefDefaultLowestLineNumber) -
    Math.min(maxNoteLineNumber, clefDefaultHighestLineNumber);
  console.debug('[PhraseOptimizer] minNoteLNo', minNoteLineNumber, 'maxNoteLno', maxNoteLineNumber, minNote, maxNote, clef);
  const innerHeightLineDiff =
    clef === "treble"
      ? Math.max(clefDefaultLowestLineNumber, minNoteLineNumber) -
      clefDefaultHighestLineNumber
      : clefDefaultLowestLineNumber -
      Math.min(maxNoteLineNumber, clefDefaultHighestLineNumber);
  console.debug(
    "[PhraseOptimizer::computeClefHeight] clefDefaultLowestLineNumber=",
    clefDefaultLowestLineNumber,
    "clefDefaultHighestLineNumber=",
    clefDefaultHighestLineNumber,
    "outerHeightLineDiff=",
    outerHeightLineDiff,
    "innerHeightLineDiff=",
    innerHeightLineDiff,
  );
  const outerHeight = (outerHeightLineDiff) / 2 * pitchLineGap;
  const innerHeight = (innerHeightLineDiff) / 2 * pitchLineGap;
  /*let lowestLineNumber = 0;
  let highestLineNumber = 0;
  if (clef === "treble") {
    lowestLineNumber = getNoteLineNumber(0, clef, ["E", 4]);
    highestLineNumber = getNoteLineNumber(0, clef, ["F", 5]);
  } else {
    lowestLineNumber = getNoteLineNumber(0, clef, ["G", 2]);
    highestLineNumber = getNoteLineNumber(0, clef, ["A", 3]);
  }
  const minLineNumber = Math.min(
    lowestLineNumber,
    getNoteLineNumber(0, clef, minNote[1] as [BaseNote, number]),
  );
  const maxLineNumber = Math.max(
    highestLineNumber,
    getNoteLineNumber(0, clef, maxNote[1] as [BaseNote, number]),
  );
  // NOTE: This doesn't currently account for finger numbers or other symbols that may be added to the staff line.
  const outerHeight =
    (minLineNumber - maxLineNumber + 1) * pitchLineGap + pitchLineGap * 0.5;
  const innerHeight =
    clef === "treble"
      ? (minLineNumber - highestLineNumber + 1) * pitchLineGap
      : (lowestLineNumber - maxLineNumber + 1) * pitchLineGap;
      */
  /*console.debug(
    "[PhraseOptimizer::computeClefHeight] Computed",
    clef,
    "clef height: minNote=",
    minNote,
    "maxNote=",
    maxNote,
    "minNoteLineNumber=",
    minNoteLineNumber,
    "maxNoteLineNumber=",
    maxNoteLineNumber,
    "innerHeight=",
    innerHeight,
    "outerHeight=",
    outerHeight,
  );*/
  return {
    innerHeight,
    outerHeight,
  };
}

export function computeStaffLineHeight(
  //cursorData: CursorData["serializedIterator"],
  config: Config,
  topClefNumExtraLinesTop: number,
  topClefNumExtraLinesBottom: number,
  bottomClefNumExtraLinesTop: number,
  bottomClefNumExtraLinesBottom: number,
): PhraseDimens {
  const topClefHeight = computeClefHeightWithExtraLines(
    "treble", 
    config, 
    topClefNumExtraLinesTop, 
    topClefNumExtraLinesBottom
  );
  const bottomClefHeight = computeClefHeightWithExtraLines(
    "bass", 
    config, 
    bottomClefNumExtraLinesTop, 
    bottomClefNumExtraLinesBottom
  );
  const { clefGap } = config;
  const clefGapWithExtraLines = clefGap + (
    (topClefNumExtraLinesBottom + bottomClefNumExtraLinesTop) / 2 + 1) 
    * config.pitchLineGap;
  return {
    bbHeight:
      topClefHeight.outerHeight + bottomClefHeight.outerHeight + clefGapWithExtraLines,
    topClefHeight,
    bottomClefHeight,
    topClefNumExtraLinesTop,
    topClefNumExtraLinesBottom,
    bottomClefNumExtraLinesTop,
    bottomClefNumExtraLinesBottom,
  };
}

const checkStaffLinesFit = (
  phraseDimens: PhraseDimens[],
  availableHeight: number,
  availableWidth: number,
  minGapBetweenStaffLines: number,
  minAspectRatio: number,
  config: Config,
): boolean => {
  const innerHeight1 =
    phraseDimens[0].topClefHeight.innerHeight +
    phraseDimens[0].bottomClefHeight.innerHeight +
    config.clefGap;
  const innerHeight2 =
    phraseDimens[1].topClefHeight.innerHeight +
    phraseDimens[1].bottomClefHeight.innerHeight +
    config.clefGap;
  const minVpHeightAvailable = availableHeight - minGapBetweenStaffLines;
  const fitsVertically =
    phraseDimens[0].bbHeight + phraseDimens[1].bbHeight <= minVpHeightAvailable;
  const minPhraseWidthRequired1 = minAspectRatio * innerHeight1;
  const minPhraseWidthRequired2 = minAspectRatio * innerHeight2;
  const fitsHorizontally =
    minPhraseWidthRequired1 <= availableWidth &&
    minPhraseWidthRequired2 <= availableWidth;
  console.debug(
    "[PhraseOptimizer] Checking fit: availableHeight=",
    availableHeight,
    "availableWidth=",
    availableWidth,
    "phrase1Height=",
    phraseDimens[0].bbHeight,
    "phrase2Height=",
    phraseDimens[1].bbHeight,
    "fitsVertically=",
    fitsVertically,
    "fitsHorizontally=",
    fitsHorizontally,
  );
  return fitsHorizontally && fitsVertically;
};

export interface PhraseDimens {
  bbHeight: number;
  topClefHeight: ClefHeight;
  bottomClefHeight: ClefHeight;
  topClefNumExtraLinesTop: number;
  topClefNumExtraLinesBottom: number;
  bottomClefNumExtraLinesTop: number;
  bottomClefNumExtraLinesBottom: number;
}

export type ResizeParams = {
  paddingY: number;
  paddingX: number;
  staffSpacingVertical: number;
  phraseRenderingConfig: Config;
  topPhraseDimens: PhraseDimens;
  bottomPhraseDimens: PhraseDimens;
};

export function optimizeStaffLineHeight(
  cursorData1: CursorData["serializedIterator"],
  cursorData2: CursorData["serializedIterator"],
  vpDimens: [number, number],
  minPadding: [number, number],
  options?: Config,
): ResizeParams {
  const [vpWidth, vpHeight] = vpDimens;
  const [minPadX, minPadY] = minPadding;
  const idealPadY = Math.max(minPadY, vpHeight * 0.15);
  const idealPadX = Math.max(minPadX, vpWidth * 0.175);
  const defaultConfig = DefaultConfig({ ...options });
  let clefGap = defaultConfig.clefGap;
  const topClefNumExtraLinesTop = 4;
  const topClefNumExtraLinesBottom = 2;
  const bottomClefNumExtraLinesTop = 2;
  const bottomClefNumExtraLinesBottom = 4;
  //const phraseDimens1 = computeStaffLineHeight(cursorData1, defaultConfig);
  //const phraseDimens2 = computeStaffLineHeight(cursorData2, defaultConfig);
  const phraseDimens = computeStaffLineHeight(
    defaultConfig, 
    topClefNumExtraLinesTop, 
    topClefNumExtraLinesBottom, 
    bottomClefNumExtraLinesTop, 
    bottomClefNumExtraLinesBottom
  );
  //const phraseDimens2 = computeStaffLineHeight(defaultConfig, 4, 2, 2, 4);
  // Resizing would be even nicer (albeit a little more complex) if we used
  // numbers relative to the height of a staff line here for the gap between
  // the two lines.
  // We could then call this function recursively until we reach a stable
  // configuration.
  // TODO: Implement that later.
  const minGapBetweenStaffLines = 10;
  let staffSpacingVertical = minGapBetweenStaffLines + defaultConfig.pitchLineGap * 2;
  //const maxGapBetweenStaffLines = 100;
  const minAspectRatio = 2.5;
  const idealAspectRatio = 4.5;
  const maxAspectRatio = 6.5;
  let phraseRenderingConfig: Config = defaultConfig;
  let paddingY: number = minPadY,
    paddingX: number = minPadX;
  console.debug(
    "[PhraseOptimizer] Trying to fit staff lines with default config:",
    defaultConfig,
    "and vpDimens:",
    vpDimens,
    "computed size:",
    phraseDimens,
    //phraseDimens2,
  );
  if (
    checkStaffLinesFit(
      //[phraseDimens1, phraseDimens2],
      [phraseDimens, phraseDimens],
      vpHeight - minPadY * 2,
      vpWidth - minPadX * 2,
      minGapBetweenStaffLines,
      minAspectRatio,
      defaultConfig,
    )
  ) {
    // If we can fit the staff line with minimum padding, while keeping sensible defaults,
    // we adjust padding and spacing to best utilize the available space.
    const availableVerticalPaddingSpace =
      //vpHeight - (phraseDimens1.bbHeight + phraseDimens2.bbHeight);
      vpHeight - (phraseDimens.bbHeight * 2);
    // If we have more than enough space, we have to then decide how to best use it.
    // Vertically, we can increase the gap between staff lines, or we can increase the
    // padding around the staff lines at the top and bottom.
    // We begin by computing the ideal vertical gap between staff lines.
    const idealStaffSpacing =
      //Math.max(phraseDimens1.bbHeight, phraseDimens2.bbHeight) * 0.5;
      phraseDimens.bbHeight * 0.5;
    if (idealStaffSpacing + idealPadY * 2 <= availableVerticalPaddingSpace) {
      staffSpacingVertical = idealStaffSpacing;
      paddingY = (availableVerticalPaddingSpace - staffSpacingVertical) / 2;
    } else {
      staffSpacingVertical = availableVerticalPaddingSpace - minPadY * 2;
    }

    // Here we prioritize making the phrase as wide as possible
    //const idealPhraseWidth = phraseDimens1.bbHeight * idealAspectRatio;
    const idealPhraseWidth = phraseDimens.bbHeight * idealAspectRatio;
    //const maxPhraseWidth = phraseDimens1.bbHeight * maxAspectRatio;
    const maxPhraseWidth = phraseDimens.bbHeight * maxAspectRatio;
    if (maxPhraseWidth + idealPadX * 2 <= vpWidth) {
      // If we have more than enough space to fit the maximum width of the phrase, while
      // keeping ideal padding, we increase the padding to limit the width.
      paddingX = (vpWidth - maxPhraseWidth) / 2;
    } else if (idealPhraseWidth + idealPadX * 2 <= vpWidth) {
      // Else, if we have enough space to fit the ideal width of the phrase,
      // while also keeping ideal padding, we use the ideal padding and let the phrase be
      // as wide as possible.
      paddingX = idealPadX;
    } else if (idealPhraseWidth + minPadX * 2 <= vpWidth) {
      // Else, if we have enough space to fit the ideal width of the phrase,
      // while keeping atleast a minimum amount of padding, we reduce the padding
      // to maintain the ideal phrase width.
      paddingX = (vpWidth - idealPhraseWidth) / 2;
    }
  } else {
    // If the default configuration doesn't fit, we try to make the phrase smaller,
    // while maintaining minimum padding.
    // First, we try reducing the gap between the two clefs to the minimum allowed.
    const minConfig = MinimumConfig();
    let newConfig = { ...defaultConfig, clefGap: minConfig.clefGap };
    clefGap = minConfig.clefGap;
    //const phraseDimens1 = computeStaffLineHeight(cursorData1, newConfig);
    //const phraseDimens2 = computeStaffLineHeight(cursorData2, newConfig);
    const phraseDimens = computeStaffLineHeight(
      newConfig, 
      topClefNumExtraLinesTop, 
      topClefNumExtraLinesBottom, 
      bottomClefNumExtraLinesTop, 
      bottomClefNumExtraLinesBottom
    );
    staffSpacingVertical = minGapBetweenStaffLines + newConfig.pitchLineGap * 2;
    if (
      checkStaffLinesFit(
        //[phraseDimens1, phraseDimens2],
        [phraseDimens, phraseDimens],
        vpHeight - minPadY * 2,
        vpWidth - minPadX * 2,
        minGapBetweenStaffLines,
        minAspectRatio,
        newConfig,
      )
    ) {
      const availableVerticalPaddingSpace =
        //vpHeight - (phraseDimens1.bbHeight + phraseDimens2.bbHeight);
        vpHeight - (phraseDimens.bbHeight * 2);
      const verticalSpaceMinusMinPadding =
        availableVerticalPaddingSpace - minPadY * 2;
      const spaceMinusStaffLineGap =
        verticalSpaceMinusMinPadding - minGapBetweenStaffLines;
      const spacePerStaffLine = spaceMinusStaffLineGap / 2;
      // When we have enough space to fit the staff line by reducing the clef gap to a minimum,
      // relax the gap to better utilize the available space.
      clefGap = spacePerStaffLine + minConfig.clefGap;
      phraseRenderingConfig = { ...newConfig, clefGap };
    } else {
      // If it still doesn't fit, we have to reduce the pitch line gap to make it fit.
      //const phraseDimens1 = computeStaffLineHeight(cursorData1, minConfig);
      //const phraseDimens2 = computeStaffLineHeight(cursorData2, minConfig);
      const phraseDimens = computeStaffLineHeight(
        minConfig, 
        topClefNumExtraLinesTop, 
        topClefNumExtraLinesBottom, 
        bottomClefNumExtraLinesTop, 
        bottomClefNumExtraLinesBottom
      );
      if (
        checkStaffLinesFit(
          //[phraseDimens1, phraseDimens2],
          [phraseDimens, phraseDimens],
          vpHeight - minPadY * 2,
          vpWidth - minPadX * 2,
          minGapBetweenStaffLines,
          minAspectRatio,
          minConfig,
        )
      ) {
        console.warn(
          "[PhraseOptimizer] Could only fit staff line by reducing to minimum config.",
        );
        phraseRenderingConfig = minConfig;
        const availableVerticalSpace = vpHeight - minPadY * 2 - minGapBetweenStaffLines - minConfig.clefGap * 2;
        const numberOfLinesInClef = topClefNumExtraLinesTop + topClefNumExtraLinesBottom + 8;
        const numberOfLinesForClefGap = 2;
        const numberOfLinesInStaff = numberOfLinesInClef * 2 + numberOfLinesForClefGap;
        /*const totalNumberOfLines = (calculateNumberOfLinesInClef(cursorData1, "treble") - 1.5)
          + (calculateNumberOfLinesInClef(cursorData1, "bass") - 1.5)
          + (calculateNumberOfLinesInClef(cursorData2, "treble") - 1.5)
          + (calculateNumberOfLinesInClef(cursorData2, "bass") - 1.5);*/
        const totalNumberOfLines = numberOfLinesInStaff * 2;
        const pitchLineGap = availableVerticalSpace / (totalNumberOfLines / 2);
        console.debug('[PhraseOptimizer] computing space for ', 
          totalNumberOfLines, 'lines, availableVerticalSpace', 
          availableVerticalSpace, 'pitchLineGap', pitchLineGap,
          'minPadY', minPadY, 'minPadX', minPadX
        );
        staffSpacingVertical = minGapBetweenStaffLines;
        phraseRenderingConfig.pitchLineGap = pitchLineGap;
        console.debug('[PhraseOptimizer] Adjusted pitchLineGap to ', pitchLineGap);
        // This is going to be WAYYYYY too complex so we'll just leave it for later.
        /*clefGap = minConfig.clefGap;
        const availableVerticalPaddingSpace = vpHeight - staffLineHeight * 2;
        const verticalSpaceMinusMinPadding = availableVerticalPaddingSpace - minPadY * 2;
        const spaceMinusStaffLineGap = verticalSpaceMinusMinPadding - minGapBetweenStaffLines;
        const spacePerStaffLine = spaceMinusStaffLineGap / 2;*/
      } else {
        console.error('[PhraseOptimizer] doesnt fit');
      }
    }
  }
  //const phraseRenderingConfig = DefaultConfig({ ...options, clefGap });
  /*const topPhraseDimens = computeStaffLineHeight(
    cursorData1,
    phraseRenderingConfig,
  );
  const bottomPhraseDimens = computeStaffLineHeight(
    cursorData2,
    phraseRenderingConfig,
  );*/
  const phraseDimensFinal = computeStaffLineHeight(
    phraseRenderingConfig,
    topClefNumExtraLinesTop,
    topClefNumExtraLinesBottom,
    bottomClefNumExtraLinesTop,
    bottomClefNumExtraLinesBottom
  );
  //console.debug('[PhraseOptimizer] topPhraseDimens', topPhraseDimens, cursorData1);
  //console.debug('[PhraseOptimizer] bottomPhraseDimens', bottomPhraseDimens, cursorData2);
  console.debug('[PhraseOptimizer] phraseDimensFinal', phraseDimensFinal);
  return {
    paddingY,
    paddingX,
    staffSpacingVertical,
    phraseRenderingConfig,
    //topPhraseDimens,
    //bottomPhraseDimens,
    topPhraseDimens: phraseDimensFinal,
    bottomPhraseDimens: phraseDimensFinal,
  };
}
